import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import {
  FileInfo,
  MenuOptionValue,
  NestedPick,
  Paginated,
  PaginationQuery,
  PaginationQueryWithFields,
} from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { InsertRentalCar, RentalCar, RentalCarNoCarDepency, UpdateRentalCar } from "./types";

export const useRentalCarApi = () => {
  const api = useApi();

  const getAdminRentalCar = useCallback(
    (id: string, signal: AbortSignal) => {
      const url = `/admin/rental_cars/${id}`;
      return useSWRMutation<RentalCar | null, CustomError, string>(url, (url) => api.get<RentalCar>({ url, signal }));
    },
    [api],
  );

  const useAdminRentalCar = useCallback(
    <K extends keyof RentalCarNoCarDepency>(
      query: PaginationQueryWithFields<NestedPick<RentalCarNoCarDepency, K>>,
      signal: AbortSignal,
      carId?: string,
    ) => {
      const url = buildUrl<NestedPick<RentalCarNoCarDepency, K>>(
        carId ? `/admin/cars/${carId}/rental_cars` : "/admin/rental_cars",
        query,
      );
      return useSWR<Paginated<NestedPick<RentalCarNoCarDepency, K>> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<NestedPick<RentalCarNoCarDepency, K>>>({ url, signal }),
      );
    },
    [api],
  );

  const exportExcel = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/rental_cars", query);
      const h = new Headers();
      h.append("Accept", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
      const response = api.get<FileInfo>({ url, signal, headers: h });
      return response;
    },
    [api],
  );

  const updateAdminRentalCar = useCallback(
    async (id: string, body: UpdateRentalCar) => {
      const url = `/admin/rental_cars/${id}`;
      await api.put<UpdateRentalCar>(url, body);
      api.mutateCache([
        url,
        `/admin/rental_cars`,
        `/admin/cars/${body.carId}`,
        `/admin/cars/${body.carId}/rental_cars`,
        `/admin/cars/${body.carId}/overview`,
      ]);
    },
    [api],
  );

  const insertAdminRentalCar = useCallback(
    async (body: InsertRentalCar) => {
      const url = `/admin/rental_cars`;
      await api.postNoResult<InsertRentalCar>(url, body);
      api.mutateCache([
        url,
        `/admin/rental_cars`,
        `/admin/cars/${body.carId}`,
        `/admin/cars/${body.carId}/rental_cars`,
        `/admin/cars/${body.carId}/overview`,
      ]);
    },
    [api],
  );

  const deleteAdminRentalCar = useCallback(
    async (id: string, carId?: string): Promise<void> => {
      const url = `/admin/rental_cars/${id}`;
      await api.del(url);
      api.mutateCache([
        url,
        `/admin/rental_cars`,
        `/admin/cars/${carId}`,
        `/admin/cars/${carId}/rental_cars`,
        `/admin/cars/${carId}/overview`,
      ]);
    },
    [api],
  );

  const getFieldFilters = useCallback(
    (fieldName: string, signal: AbortSignal) => {
      const url = `/admin/rental_cars/filters?field=${fieldName}`;
      return useSWRMutation<MenuOptionValue[] | null, CustomError, string>(url, (url) =>
        api.get<MenuOptionValue[]>({ url, signal }),
      );
    },
    [api],
  );

  return {
    deleteAdminRentalCar,
    insertAdminRentalCar,
    updateAdminRentalCar,
    useAdminRentalCar,
    getAdminRentalCar,
    getFieldFilters,
    exportExcel,
  };
};
